import React, { useEffect, useMemo } from 'react';
import { Modal, Typography } from 'antd';
import styled, { createGlobalStyle } from 'styled-components';
import dayjs from 'dayjs';
import { EWorkshopPartType } from 'data/types/workshops.types';
import { workshopsApi } from 'redux/apiSlice/workshops.slice';
import { useGetOrganizationKitOrderHistoryQuery } from 'redux/apiSlice/organizationKitInventory.slice';

const { Text } = Typography;

const GlobalModalStyle = createGlobalStyle`
  .history-modal-wrapper .ant-modal-wrap {
    overflow: hidden;
  }
  
  .history-modal-wrapper .ant-modal {
    padding: 0;
  }
  
  .history-modal-wrapper .ant-modal-mask {
    background-color: rgba(0, 0, 0, 0.65);
  }
  
  .history-modal-wrapper .ant-modal-content {
    border: none !important;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2) !important;
  }
`;

const StyledModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    border: none;
  }

  .ant-modal-header {
    display: none;
  }

  .ant-modal-body {
    padding: 0;
  }

  .ant-modal-footer {
    display: none;
  }

  .ant-modal-close {
    top: 20px;
    right: 20px;
  }

  .ant-modal-close-x {
    font-size: 42px;
    font-weight: 300;
    width: 24px;
    height: 24px;
    line-height: 24px;
  }
`;

const ModalContainer = styled.div`
  padding: 0;
`;

const ModalHeader = styled.div`
  padding: 0;
`;

const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 16px;
  margin: 0;
  padding: 0;
  color: #000;
  line-height: 1.2;
`;

const ModalContent = styled.div`
  padding: 16px 0px;
`;

const HistoryScrollContainer = styled.div`
  max-height: 500px;
  overflow-y: auto;
  padding-right: 8px;
  margin-bottom: 16px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }

  @media (max-height: 700px) {
    max-height: 200px;
  }

  @media (min-height: 900px) {
    max-height: 400px;
  }
`;

const DateHeader = styled(Text)`
  display: flex;
  height: 23px;
  flex-direction: column;
  justify-content: center;
  align-self: stretch;
  color: var(--Color_grey_60, #808080);
  font-family: Lato;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  margin-bottom: 8px;
`;

const SectionHeader = styled(Text)`
  display: flex;
  height: 23px;
  flex-direction: column;
  justify-content: center;
  align-self: stretch;
  color: var(--Black-Heading, #1b1a1c);
  font-family: Lato;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 23px;
  margin-bottom: 8px;
`;

const ItemRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  align-items: center;
`;

const ItemName = styled(Text)`
  color: var(--Black-Heading, #1b1a1c);
  font-family: Lato;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 23px;
`;

const ItemQuantity = styled(Text)`
  color: var(--Black-Heading, #1b1a1c);
  font-family: Lato;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 23px;
`;

const Divider = styled.div`
  height: 1px;
  background-color: #e8e8e8;
  margin: 16px 0;
  width: 100%;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 0;
`;

const GotItButton = styled.button`
  padding: 10px 20px;
  border-radius: 8px;
  background-color: #0077a0;
  color: white;
  border: none;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;

  &:hover {
    background-color: #00628a;
  }
`;

const LoadingText = styled.div`
  text-align: center;
  padding: 20px;
  color: #808080;
`;

const NoDataText = styled.div`
  text-align: center;
  padding: 20px;
  color: #808080;
`;

interface HistoryItem {
  id: string;
  name: string;
  quantity: number;
}

interface HistorySection {
  title: string;
  items: HistoryItem[];
}

interface OrderHistoryEntry {
  date: string;
  sections: HistorySection[];
}

export interface OrderHistoryModalProps {
  visible: boolean;
  onClose: () => void;
  workshopId?: string;
  activePart?: EWorkshopPartType;
  organizationId?: string;
  context?: 'workshop' | 'organization';
}

const OrderHistoryModal: React.FC<OrderHistoryModalProps> = ({
  visible,
  onClose,
  workshopId,
  activePart,
  organizationId,
  context = 'workshop',
}) => {
  const {
    data: workshopData,
    isLoading: isWorkshopLoading,
    refetch: refetchWorkshop,
  } = workshopsApi.endpoints.getWorkshopKitOrderHistory.useQuery(
    workshopId || '',
    {
      skip: !visible || context !== 'workshop' || !workshopId,
    },
  );

  const {
    data: organizationData,
    isLoading: isOrganizationLoading,
    refetch: refetchOrganization,
  } = useGetOrganizationKitOrderHistoryQuery(organizationId || '', {
    skip: !visible || context !== 'organization' || !organizationId,
  });

  useEffect(() => {
    if (visible) {
      if (context === 'workshop') {
        refetchWorkshop();
      } else {
        refetchOrganization();
      }
    }
  }, [visible, context, refetchWorkshop, refetchOrganization]);

  const orderHistory = useMemo(() => {
    if (context === 'workshop') {
      if (
        !workshopData ||
        !workshopData.orderHistory ||
        !activePart ||
        !workshopData.orderHistory[activePart]
      ) {
        return [];
      }

      const partHistory = workshopData.orderHistory[activePart];
      const entries: OrderHistoryEntry[] = [];

      const dateKeys = Object.keys(partHistory || {});
      const sortedDates = dateKeys.sort(
        (a, b) => new Date(b).getTime() - new Date(a).getTime(),
      );

      sortedDates.forEach(dateString => {
        const items = partHistory?.[dateString];
        if (!items || items.length === 0) return;

        const formattedDate = dayjs(dateString).format('MMM DD, YYYY');

        const editedItems = items.filter(
          (item: any) => item.operation === 'Edited Items',
        );
        const orderedItems = items.filter(
          (item: any) => item.operation === 'Kits Ordered',
        );

        const sections: HistorySection[] = [];

        if (editedItems.length > 0) {
          sections.push({
            title: 'Edited Items:',
            items: editedItems.map((item: any) => ({
              id: item.workshopKitId,
              name: item.kitName,
              quantity: item.orderCount,
            })),
          });
        }

        if (orderedItems.length > 0) {
          sections.push({
            title: 'Kits Ordered:',
            items: orderedItems.map((item: any) => ({
              id: item.workshopKitId,
              name: item.kitName,
              quantity: item.orderCount,
            })),
          });
        }

        if (sections.length > 0) {
          entries.push({
            date: formattedDate,
            sections,
          });
        }
      });

      return entries;
    }

    if (!organizationData || !organizationData.inventoryHistory) {
      return [];
    }

    const entries: OrderHistoryEntry[] = [];
    const dateKeys = Object.keys(organizationData.inventoryHistory || {});
    const sortedDates = dateKeys.sort(
      (a, b) => new Date(b).getTime() - new Date(a).getTime(),
    );

    sortedDates.forEach(dateString => {
      const items = organizationData.inventoryHistory[dateString];
      if (!items || items.length === 0) return;

      const formattedDate = dayjs(dateString).format('MMM DD, YYYY');

      const editedItems = items.filter(
        (item: any) => item.operation === 'Edited Items',
      );
      const orderedItems = items.filter(
        (item: any) => item.operation === 'Kits Ordered',
      );

      const sections: HistorySection[] = [];

      if (editedItems.length > 0) {
        sections.push({
          title: 'Edited Items:',
          items: editedItems.map((item: any) => ({
            id: item.workshopKitId,
            name: item.kitName,
            quantity: item.quantityChange,
          })),
        });
      }

      if (orderedItems.length > 0) {
        sections.push({
          title: 'Kits Ordered:',
          items: orderedItems.map((item: any) => ({
            id: item.workshopKitId,
            name: item.kitName,
            quantity: item.quantityChange,
          })),
        });
      }

      if (sections.length > 0) {
        entries.push({
          date: formattedDate,
          sections,
        });
      }
    });

    return entries;
  }, [workshopData, organizationData, activePart, context]);

  const isLoading =
    context === 'workshop' ? isWorkshopLoading : isOrganizationLoading;

  const renderContent = () => {
    if (isLoading) {
      return <LoadingText>Loading order history...</LoadingText>;
    }

    if (orderHistory.length === 0) {
      const noDataMessage =
        context === 'workshop'
          ? 'No order history found for this part.'
          : 'No order history found for this organization.';
      return <NoDataText>{noDataMessage}</NoDataText>;
    }

    return orderHistory.map((order, index) => (
      <React.Fragment key={`order-${order.date}`}>
        <DateHeader>{order.date}</DateHeader>

        {order.sections.map(section => (
          <div key={`section-${order.date}-${section.title}`}>
            <SectionHeader>{section.title}</SectionHeader>

            {section.items.map(item => (
              <ItemRow key={`item-${item.id}`}>
                <ItemName>{item.name}</ItemName>
                <ItemQuantity>{item.quantity}</ItemQuantity>
              </ItemRow>
            ))}
          </div>
        ))}

        {/* Only add a divider if this isn't the last entry */}
        {index < orderHistory.length - 1 && <Divider />}
      </React.Fragment>
    ));
  };

  return (
    <>
      <GlobalModalStyle />
      <StyledModal
        open={visible}
        onCancel={onClose}
        width={600}
        closeIcon={<span>×</span>}
        footer={null}
        centered
        style={{ padding: 0 }}
        styles={{ body: { padding: 0 } }}
        wrapClassName="history-modal-wrapper"
      >
        <ModalContainer>
          <ModalHeader>
            <ModalTitle>Order History</ModalTitle>
          </ModalHeader>

          <ModalContent>
            <HistoryScrollContainer>{renderContent()}</HistoryScrollContainer>
          </ModalContent>

          <ModalFooter>
            <GotItButton type="button" onClick={onClose}>
              Got it
            </GotItButton>
          </ModalFooter>
        </ModalContainer>
      </StyledModal>
    </>
  );
};

export default OrderHistoryModal;
